import { useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '@yorukiri/ui/tokens';

type Props = {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  formatValue?: (v: number) => string;
  width?: number;
};

// Horizontal slider with a numeric readout on the right. Drag anywhere on the
// track to set the value; click the readout to type exactly. Used for target
// retention in deck config; could power any bounded integer setting.
export function Slider({
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  formatValue,
  width = 220,
}: Props) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));

  const pct = ((value - min) / (max - min)) * 100;

  const setFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const raw = ((clientX - rect.left) / rect.width) * (max - min) + min;
      const snapped = Math.min(max, Math.max(min, Math.round(raw / step) * step));
      if (snapped !== value) onChange(snapped);
    },
    [max, min, onChange, step, value],
  );

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => setFromClientX(e.clientX);
    const onUp = () => setDragging(false);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, setFromClientX]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(Math.min(max, value + step));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(Math.max(min, value - step));
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(min);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(max);
    }
  };

  const commit = () => {
    const n = Number(draft);
    if (Number.isFinite(n)) onChange(Math.min(max, Math.max(min, Math.round(n / step) * step)));
    setEditing(false);
  };

  const display = formatValue ? formatValue(value) : String(value);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        onKeyDown={handleKey}
        onPointerDown={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement).focus();
          setDragging(true);
          setFromClientX(e.clientX);
        }}
        style={{
          width,
          height: 28,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          outline: 'none',
          userSelect: 'none',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 4,
            background: colors.line2,
            borderRadius: 2,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: 0,
            width: `${pct}%`,
            height: 4,
            background: colors.accent,
            borderRadius: 2,
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `calc(${pct}% - 7px)`,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: colors.surface,
            border: `2px solid ${colors.accent}`,
            boxShadow: dragging
              ? `0 0 0 6px ${colors.accent}26`
              : '0 1px 2px rgba(0,0,0,0.08)',
            transition: dragging ? 'none' : 'box-shadow 0.12s',
          }}
        />
      </div>

      {editing ? (
        <input
          autoFocus
          type="text"
          inputMode="numeric"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDraft(String(value));
              setEditing(false);
            }
          }}
          style={{
            width: 52,
            padding: '4px 6px',
            border: `1px solid ${colors.accent}`,
            borderRadius: 4,
            fontFamily: 'inherit',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            background: colors.surface,
            color: colors.ink,
            outline: 'none',
          }}
        />
      ) : (
        <button
          onClick={() => {
            setDraft(String(value));
            setEditing(true);
          }}
          style={{
            minWidth: 52,
            background: 'transparent',
            border: 'none',
            padding: 0,
            fontFamily: 'inherit',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'right',
            cursor: 'text',
            color: colors.ink,
          }}
        >
          {display}
          {suffix && <span style={{ color: colors.ink3, marginLeft: 2 }}>{suffix}</span>}
        </button>
      )}
    </div>
  );
}
